'use client';

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
    type VideofeedConfig,
    VideofeedConfigClient,
    type VideofeedItemPublicScope,
    VideofeedType,
} from '@common/clients/api';
import { fetchVideofeedConfig } from '@common/handlers/fetchVideofeedConfig';
import { WebpWidthEnum } from '@common/types/WebpWidth';
import { useContextData } from '@common/useContextData';
import { ScrollTracker } from '@web/atoms/ScrollTracker';
import { fetchVideofeed } from '@web/handlers/fetchVideofeed';
import { VideoPlayer } from '@web/molecules/VideoPlayer';
import type { PlayVideoCallback, Video } from '@web/molecules/VideoPlayer/VideoPlayer.types';
import styles from '@web/organisms/VideoCarousel/VideoCarousel.module.scss';
import { SCROLL_TRACKING_CONFIGS } from '@web/utils/gtm-tracker/config';
import { ComponentScrollTargets } from '@web/utils/gtm-tracker/types/scroll/base';

import { Carousel } from './molecules/Carousel';
import { type VideoCarouselProps } from './VideoCarrousel.types';

const AUTOPLAY_POSITIONS = [-1, 0];
const PRELOAD_POSITIONS = [-1, 0, 1];

export const Component = ({ mainVideo, videos: initialVideos, placement }: VideoCarouselProps) => {
    const context = useContextData();
    const carouselRef = useRef<HTMLElement>(null);
    const [playVideoCallback, setPlayVideoCallback] = useState<PlayVideoCallback>();
    const [videos, setVideos] = useState<VideofeedItemPublicScope[] | undefined>(initialVideos);
    const [videofeedConfig, setVideofeedConfig] = useState<VideofeedConfig | undefined>(undefined);
    const [activeVideoID, setActiveVideoID] = useState<string | undefined>();

    const onSelectVideo = useCallback(
        (videoID: string) => {
            if (!playVideoCallback) {
                return;
            }
            playVideoCallback(videoID);
        },
        [playVideoCallback],
    );

    useEffect(() => {
        if (videos === undefined) {
            void fetchVideofeed({ contextData: context }).then((videos) => {
                setVideos(videos);
            });
        }
        void fetchVideofeedConfig(context, VideofeedConfigClient.DEFAULT, VideofeedType.DEFAULT).then(
            (config) => {
                setVideofeedConfig(config);
            },
        );
    }, [videos, context]);

    const videoList: VideofeedItemPublicScope[] = useMemo(() => {
        let videoList = [...(videos || [])];
        if (mainVideo) {
            videoList = videoList.filter((video) => video.id !== mainVideo.id);
            videoList.unshift(mainVideo);
        }
        return videoList;
    }, [mainVideo, videos]);

    const playlist: Video[] = useMemo(
        () =>
            (videoList || []).map((video) => ({
                mediaid: video.id.toString(),
                title: video.domainTitle || video.title,
                file: video.url || '',
                vendor: video.vendor,
                sources: [],
                image: (video?.preview || video?.thumbnail || '').replace(
                    '{WebpWidth}',
                    `${WebpWidthEnum.WIDTH_900}`,
                ),
                duration: video.duration,
                link: video.path ? (/^\/\//.test(video.path) ? `https${video.path}` : video.path) : undefined,
            })),
        [videoList],
    );

    if (!videos) {
        return <aside className={styles.VideoCarousel} />;
    } else if (!playlist.length) {
        return;
    }

    const isAutoPlay =
        mainVideo?.position !== undefined ? AUTOPLAY_POSITIONS.includes(mainVideo.position) : false;
    const isPreload =
        mainVideo?.position !== undefined ? PRELOAD_POSITIONS.includes(mainVideo.position) : false;
    return (
        <aside className={styles.VideoCarousel} ref={carouselRef}>
            <VideoPlayer
                placement={placement}
                isPreload={isPreload}
                isAutoPlay={isAutoPlay}
                playlist={playlist}
                videofeedConfig={videofeedConfig}
                setActiveVideoID={setActiveVideoID}
                registerPlayVideoCallback={(callback) => setPlayVideoCallback(() => callback)}
            />
            <Carousel videos={videoList} activeVideoID={activeVideoID} onItemClick={onSelectVideo} />
            <ScrollTracker
                targetRef={carouselRef}
                {...SCROLL_TRACKING_CONFIGS[ComponentScrollTargets.VIDEO_CAROUSEL]}
                label={activeVideoID}
            />
        </aside>
    );
};

Component.displayName = 'VideoCarousel';
export const VideoCarousel = memo(Component);
